/**
 * angular-bridge.ts
 *
 * THE ONLY FILE that accesses window.__ngInjector.
 * React code must use the typed getters below — never access the injector directly.
 *
 * As each Angular service is replaced by a React hook or Zustand store, remove
 * its getter from this file. When this file has zero Angular/injector references,
 * Phase 6 (Angular removal) can begin.
 */

// Set by main.js immediately after angular.bootstrap() completes.
let _injector: angular.auto.IInjectorService | null = null;

export function setAngularInjector(injector: angular.auto.IInjectorService): void {
  _injector = injector;
  (window as any).__ngInjector = injector;
}

function get<T>(token: string): T {
  if (!_injector) throw new Error(`Angular injector not ready — cannot get ${token}`);
  return _injector.get<T>(token);
}

// ─── Typed service getters ────────────────────────────────────────────────────

/** App config: server base URL, client ID, env settings, etc. */
export function getConfigService() {
  return get<import('../../scripts/services/config/ConfigService').IConfigService>('ConfigService');
}

/** Current user account: email, name, roles, etc. */
export function getAccountService() {
  // AccountService is typed loosely here because its interface is not exported separately.
  return get<any>('AccountService');
}

export function isSuperAdmin(): boolean {
  try { return get<any>('AccountService').IsSuperAdmin() ?? false; } catch { return false; }
}

/** OAuth token storage: access token, refresh, existence checks. */
export function getTokenService() {
  return get<any>('TokenService');
}

/**
 * In-app notification toasts (success, error, info).
 *
 * The underlying Angular service only pushes to an in-memory array; the toast UI re-renders
 * on the next `$digest`. When called from a React/TanStack callback we're outside Angular's
 * digest, so mutating calls are wrapped to schedule one via `$applyAsync` (safe whether or not
 * a digest is already running). Read methods are passed through untouched to avoid digest loops.
 */
export function getNotificationService() {
  type INotificationService = import('../../scripts/services/notification').INotificationService;
  const svc = get<INotificationService>('NotificationService');
  const rootScope = get<angular.IRootScopeService>('$rootScope');
  return new Proxy(svc, {
    get(target, prop, receiver) {
      const value = Reflect.get(target, prop, receiver);
      if (typeof value !== 'function') return value;
      const name = String(prop);
      if (!/^(add|dismiss)/.test(name)) return value.bind(target);
      return (...args: unknown[]) => {
        const result = (value as (...a: unknown[]) => unknown).apply(target, args);
        rootScope.$applyAsync();
        return result;
      };
    },
  });
}

/** Angular root scope — use only for cross-framework event listening during migration. */
export function getRootScope() {
  return get<angular.IRootScopeService>('$rootScope');
}

/** Current Angular route params (e.g. projectId, collectionId). */
export function getRouteParams(): Record<string, string> {
  return get<Record<string, string>>('$routeParams');
}

/** Large logo URL for the current theme (falls back to the default Folio logo). */
export function getThemeLargeLogo(): string {
  const theme = get<any>('ThemeService').getCurrentTheme();
  return theme?.largeLogo ?? '/images/FolioLogoColor.svg';
}


/** Sets the active left-nav item (mirrors NavigationService.setActiveTab). */
export function setNavActiveTab(name: string, subname?: string): void {
  get<any>('NavigationService').setActiveTab(name, subname);
}



/** Sets the Angular ProjectService current project, populating the nav sidebar with project context. */
export function setCurrentProject(project: any): void {
  get<any>('ProjectService').SetCurrentProject(project);
}

/**
 * The project/collection the Angular nav considers current. The search-results layer is
 * not route-mounted, so it has no route params to read an id from — SearchController
 * reads these same two services (`SearchController:48-49`).
 */
export function getCurrentProject(): any {
  return get<any>('ProjectService').GetCurrentProject() ?? null;
}

export function getCurrentCollection(): any {
  return get<any>('CollectionService').GetCurrentCollection() ?? null;
}

/**
 * Raises the search layer. The basic nav input pushes it itself when its query goes
 * empty→non-empty, but every other entry point (saved searches via
 * `Search.SearchExecuted.Externally`, the advanced builder, voice search) relied on
 * SearchController doing it in its own Search.SearchExecuted handler — and that
 * controller is not instantiated when the React grid flag is on. pushLayer ignores a
 * layer that is already on top, so double-pushing with the nav input is harmless.
 */
export function pushSearchLayer(): void {
  const layerService = get<any>('LayerService');
  layerService.pushLayer(layerService.LAYERS.SEARCH);
}

/**
 * Drops every layer above 'main'. Angular's links did this themselves — `iio-href`
 * with `layer="main"` calls popToMain on click (href.js:64) — so a React link that
 * only changes the hash leaves the search (or resource) layer covering the route it
 * just navigated to. No-op when the stack is already just 'main'.
 *
 * Silent when the injector isn't ready: navigation must not depend on Angular being up.
 */
export function popToMainLayer(): void {
  if (!_injector) return;
  get<any>('LayerService').popToMain();
}

/**
 * Clears the Angular CollectionService current collection. Resets the top search
 * bar scope from "This collection" back to "All collections" when returning to a
 * project-level (non-collection) page.
 */
export function clearCurrentCollection(): void {
  get<any>('CollectionService').ClearCurrentCollection();
}

/**
 * Clears the Angular ProjectService current project. Hides the top search bar
 * (and clears the left nav/sidebar) when leaving project scope for the project list.
 */
export function clearCurrentProject(): void {
  get<any>('ProjectService').ClearCurrentProject();
}

// Project the saved grid sort/filter belong to. Persisted with them, so a reload into a
// different project still clears them.
const GRID_FILTER_PROJECT_PREF = 'GridColumnsFilterProjectId';

/** Clears grid sort/filter (not column layout) when they belong to another project. */
export function resetGridPreferencesOnProjectChange(newProjectId: string): void {
  if (!newProjectId) return;
  const prefs = get<any>('PreferenceService');
  if (prefs.Get(GRID_FILTER_PROJECT_PREF) !== newProjectId) {
    prefs.ResetGridPreferences();
    prefs.Set(GRID_FILTER_PROJECT_PREF, newProjectId);
  }
}

/**
 * Broadcasts an Angular $rootScope event from React. Needed while parts of the shell are
 * still Angular (the header task dropdown listens for MonitorableUpdated/Completed).
 * Safe to call before the injector exists — the event is dropped rather than throwing.
 */
export function broadcastAngularEvent(eventName: string, ...args: any[]): void {
  if (!_injector) return;
  get<any>('$rootScope').$broadcast(eventName, ...args);
}

/** Registers a callback for an Angular $rootScope event. Returns an unsubscribe function. */
export function onAngularEvent(eventName: string, callback: (...args: any[]) => void): () => void {
  const rootScope = get<any>('$rootScope');
  const unregister = rootScope.$on(eventName, (event: any, ...args: any[]) => {
    callback(...args);
  });
  return () => unregister();
}



/**
 * Persists the set of collections the Tag Explorer should scope to (mirrors the old
 * ProjectDetailController `_saveSelectedCollections`). The browse page reads this via
 * `CollectionService.FindSelectedOrAll`; an empty set means "all collections".
 */
export function setSelectedCollections(collectionIds: string[]): void {
  get<any>('PreferenceService').SelectedCollections = collectionIds.map(id => ({ id }));
}

/** Sets the Tag Explorer filter to the given tag and navigates to the browse page. */
export function navigateToTagExplorer(projectId: string, tagId: string): void {
  get<any>('PreferenceService').CurrentFilter = {
    size: 1,
    projectId,
    tags: { and: [tagId], or: [], not: [] },
  };
  window.location.hash = `!/project/${projectId}/browse`;
}

/**
 * The current Tag Explorer filter ({size, projectId, tags:{and,or,not}}) or null.
 * The browse page reads this to scope its query; it is set by navigateToTagExplorer
 * (or the Angular resourceFilter builder) before navigation.
 */
export function getCurrentFilter(): any {
  return get<any>('PreferenceService').CurrentFilter ?? null;
}

/**
 * Persists the Tag Explorer filter ({size, projectId, tags:{and,or,not}} with tag ids).
 * Assigning PreferenceService.CurrentFilter writes it to the cookie, exactly as the
 * Angular resourceFilter builder does, so a later reload/back rehydrates the filter.
 */
export function setCurrentFilter(filter: unknown): void {
  get<any>('PreferenceService').CurrentFilter = filter;
}

/** Reads a user preference via Angular's PreferenceService (e.g. "DisplayMode"). */
export function getPreference<T = unknown>(key: string): T | undefined {
  return get<any>('PreferenceService').Get(key);
}

/** Persists a user preference via Angular's PreferenceService (writes the cookie). */
export function setPreference(key: string, value: unknown): void {
  get<any>('PreferenceService').Set(key, value);
}

/**
 * Broadcasts a BasicSearch query to the Angular search system (only effective on the project detail page).
 * Pass context=0 for Collection scope, context=1 (default) for Project scope (all collections).
 */
export function broadcastBasicSearch(query: string, context: 0 | 1 = 1): void {
  const $rootScope = get<any>('$rootScope');
  // The search directive's handler calls $apply, which throws mid-digest.
  if ($rootScope.$$phase) {
    setTimeout(() => broadcastBasicSearch(query, context));
    return;
  }
  // Picked up by the search directive when it links, if it hasn't yet; its handler clears it.
  $rootScope.pendingBasicSearch = { query, context };
  $rootScope.$broadcast('SearchContext.Changed.Externally', context);
  $rootScope.$broadcast('BasicSearch.Triggered', query);
}

/**
 * Reports the outcome of a basic search to the Angular search input, which paints itself
 * red on an error (`iio-input-notice`) and clears that on the next success. Angular's
 * BasicSearchService broadcast these off its own promise; the React datasource replaced
 * that service, so it has to raise them.
 */
export function broadcastSearchResult(ok: boolean): void {
  try {
    get<any>('$rootScope').$broadcast(ok ? 'Search.SearchSuccess' : 'Search.SearchError');
  } catch {
    /* Angular not mounted (tests, standalone React) */
  }
}

/**
 * Sets Angular's current collection (CollectionService), so a Collection-scoped basic
 * search (broadcastBasicSearch context 0) resolves to this collection — SearchController
 * reads GetCurrentCollection() for the Collection scope. Mirrors CollectionController's
 * LoadCollection → SetCurrentCollection.
 */
export function setCurrentCollection(collection: unknown): void {
  try { get<any>('CollectionService').SetCurrentCollection(collection); } catch {}
}

/** Mirrors TrackService.TrackEvent — fires gtag + console.log for QA click tracking. */
export function trackEvent(category: string, action: string, label?: string): void {
  try { get<any>('TrackService').TrackEvent(category, action, label); } catch {}
}

/** Writes an audit log entry for the given project. Mirrors AuditLogService.Audit. */
export function auditLogEvent(projectId: string, description: string, data: Record<string, unknown>): void {
  try { get<any>('AuditLogService').Audit(projectId, description, data); } catch {}
}

export function openResourceViewer(config: {
  projectId: string;
  collectionId: string;
  resourceId: string;
}): void {
  import('../stores/resourceViewerStore').then(({ useResourceViewerStore }) => {
    useResourceViewerStore.getState().open(config);
  });
}

/** Opens the Upgrade (credit card) Angular modal. */
export function openCreditCardDialog(project: any): void {
  const modal = get<any>('$uibModal');
  const rootScope = get<any>('$rootScope');
  modal.open({
    backdrop: true,
    keyboard: true,
    backdropClick: true,
    templateUrl: 'partials/dialog_credit_card.html',
    controller: 'CreditCardModalController',
    scope: rootScope.$new(),
    resolve: { project: () => project, message: () => '' },
  });
}

/**
 * Reads the Company/Firm filter selected on the Project List page, persisted via
 * PreferenceService (cookie, ~1yr expiry) so it survives across windows/tabs — the same
 * mechanism used for the browse/search List View vs. Grid View preference.
 */
export function getProjectListOrgFilter(): string | null {
  try { return get<any>('PreferenceService').Get('ProjectListOrgFilter') ?? null; } catch { return null; }
}

/** Persists the Company/Firm filter selected on the Project List page. */
export function setProjectListOrgFilter(orgId: string | null): void {
  try { get<any>('PreferenceService').Set('ProjectListOrgFilter', orgId); } catch {}
}
